import { DirectEntryStepPaths } from '../../modules/application/direct-entry/shared/constants/direct-entry-steps';
import { routeMatchesRouteTemplate } from '../functions/string-helpers';
import { RouteKey } from '../models/route-key';

class ApplicationRoutes {
    static readonly rootUrl: string = 'application';
    static readonly applicationIdParameter: string = ':id';
    static readonly rootUrlWithId: string = `${this.rootUrl}/${this.applicationIdParameter}`;

    static readonly directEntryBaseUrl: string = 'direct-entry';
    static readonly wizardBaseUrl: string = 'wizard';
    static readonly newApplicationBaseUrl: string = 'new';
    static readonly coordinationBaseUrl: string = 'coordination';

    static readonly contactRequest: string = 'contact-request';
    static readonly unsupported: string = `${this.applicationIdParameter}/${this.contactRequest}`;

    /**
     * Constructs a full route when the route does not require an application ID.
     * @param route
     * @returns A navigable URL string for the specified route.
     */
    static getFullRoute(route: RouteKey<typeof ApplicationRoutes>) {
        return `/${this.rootUrl}/${ApplicationRoutes[route]}`;
    }

    /**
     * Constructs a complete URL path for application routes that require an application ID
     * using the root URL and the application ID parameter.
     * @param route
     * @param applicationId
     * @returns A fully formed URL path with the application ID inserted in the correct position
     */
    static getFullRouteWithApplicationId(
        route: RouteKey<typeof ApplicationRoutes>,
        applicationId: string,
    ) {
        return `/${this.rootUrl}/${ApplicationRoutes[route]}`.replace(
            this.applicationIdParameter,
            applicationId,
        );
    }

    static isApplicationRouteWhereStepsCannotBeDirectlyNavigatedTo(
        route: string,
    ) {
        return (
            this.isNewApplicationRoute(route) ||
            this.isCoordinationApplicationRoute(route)
        );
    }

    static isDirectEntryRoute(route: string) {
        return route.includes(`/${this.directEntryBaseUrl}/`);
    }

    static isNewApplicationRoute(route: string) {
        return route.includes(`/${this.newApplicationBaseUrl}/`);
    }

    static isCoordinationApplicationRoute(route: string) {
        return route.includes(`/${this.coordinationBaseUrl}/`);
    }

    static isCoordinationResultsRoute(route: string) {
        return route.includes(
            `/${this.coordinationBaseUrl}/${this.coordinationResultsPath}`,
        );
    }

    static IsDirectEntryApplicationRoute(route: string) {
        return route.includes(`/${this.directEntryBaseUrl}/`);
    }

    static isUnsupportedApplicationRoute(route: string) {
        return route.includes(`/${this.contactRequest}`);
    }

    // New Application
    static contactLookup = `${this.newApplicationBaseUrl}/contact-lookup`;
    static purposeOfFiling = `${this.newApplicationBaseUrl}/purpose-of-filing`;
    static quickStart = `${this.newApplicationBaseUrl}/quick-start`;

    // Direct Entry
    static readonly directEntryRootUrl: string = `${this.rootUrl}/${this.applicationIdParameter}/${this.directEntryBaseUrl}`;

    /**
     * Constructs a full Direct Entry route with the application ID. This will use the base Direct Entry URL
     * and the specified route to create a complete URL path.
     * @param route
     * @param applicationId
     * @returns A fully formed Direct Entry URL path with the application ID inserted in the correct position
     */
    static getDirectEntryFullRouteWithApplicationId(
        route: (typeof DirectEntryStepPaths)[keyof typeof DirectEntryStepPaths],
        applicationId: string,
    ) {
        return `/${this.rootUrl}/${this.applicationIdParameter}/${this.directEntryBaseUrl}/${route}`.replace(
            this.applicationIdParameter,
            applicationId,
        );
    }

    // Coordination
    private static coordinationResultsPath = `results`;

    static coordinationParameters = `${this.applicationIdParameter}/${this.coordinationBaseUrl}/parameters`;
    static coordinationResults = `${this.applicationIdParameter}/${this.coordinationBaseUrl}/${this.coordinationResultsPath}`;
}

class ServiceFulfillmentRoutes {
    static readonly rootUrl: string = 'services';

    static getFullRoute(route: RouteKey<typeof ServiceFulfillmentRoutes>) {
        return `/${this.rootUrl}/${ServiceFulfillmentRoutes[route]}`;
    }

    static INDEX = '';
}

class OrderRoutes {
    static readonly rootUrl: string = 'orders';
    static readonly orderIdParameter: string = ':id';

    static readonly rootUrlWithId: string = `${this.rootUrl}/${this.orderIdParameter}`;
    static readonly paymentDetails: string = `${this.rootUrlWithId}/payment-details`;

    static getFullRoute(route: RouteKey<typeof OrderRoutes>) {
        return `/${this.rootUrl}/${OrderRoutes[route]}`;
    }

    static getOrderSummaryRoute(id: number): string {
        return `/${this.rootUrlWithId}`.replace(
            this.orderIdParameter,
            id.toString(),
        );
    }

    static INDEX = '';
}

class PaymentRoutes {
    static readonly rootUrl: string = 'payments';

    static getFullRoute(route: RouteKey<typeof PaymentRoutes>) {
        return `/${this.rootUrl}/${PaymentRoutes[route]}`;
    }

    static INDEX = '';
}

export class UserRoutes {
    static getRootUrl(): string {
        return 'users';
    }

    static getFullRoute(route: RouteKey<typeof UserRoutes>) {
        return `/${this.getRootUrl()}/${UserRoutes[route]}`;
    }

    static INDEX = '';
    static REVIEW = ':id/review';
    static EDIT = ':id/edit';
}

export class ProfileRoutes {
    static getRootUrl(): string {
        return 'profile';
    }

    static getFullRoute(route: RouteKey<typeof ProfileRoutes>) {
        return `/${this.getRootUrl()}/${ProfileRoutes[route]}`;
    }

    static INDEX = '';
    static EDIT = 'edit';
}

export class DashboardRoutes {
    static getRootUrl(): string {
        return 'dashboard';
    }

    static getFullRoute(route: RouteKey<typeof DashboardRoutes>) {
        return `/${this.getRootUrl()}/${DashboardRoutes[route]}`;
    }

    static INDEX = '';
}

export class ReportsRoutes {
    static getRootUrl(): string {
        return 'reports';
    }

    static getFullRoute(route: RouteKey<typeof ReportsRoutes>) {
        return `/${this.getRootUrl()}/${ReportsRoutes[route]}`;
    }

    static INDEX = '';
}

export class TemplateRoutes {
    static getRootUrl(): string {
        return 'templates';
    }

    static getFullRoute(route: RouteKey<typeof TemplateRoutes>) {
        return `/${this.getRootUrl()}/${TemplateRoutes[route]}`;
    }

    static INDEX = '';
}

export class HomeRoutes {
    static getHomeUrl(): string {
        return `/`;
    }
}

export class GoRoutes {
    static readonly rootUrl: string = 'go';
    static readonly newApplicationBaseUrl: string = 'applications/new';
    static readonly newApplicationRootUrl: string = `${this.rootUrl}/${this.newApplicationBaseUrl}`;
    static readonly applicationIdParameter: string = ':id';
    static readonly applicationRootUrl: string = `${this.rootUrl}/applications`;
    static readonly applicationIdBaseUrl = `applications/${this.applicationIdParameter}`;

    static getFullRoute(route: RouteKey<typeof GoRoutes>) {
        return `/${this.rootUrl}/${GoRoutes[route]}`;
    }

    static isParametersRoute(route: string): boolean {
        return (
            route.includes(this.getFullRoute('parameters')) ||
            routeMatchesRouteTemplate(
                route,
                this.getFullRouteWithApplicationId(
                    'parametersWithApplicationId',
                    this.applicationIdParameter,
                ),
            )
        );
    }

    static isConfirmParametersRoute(route: string): boolean {
        return (
            route.includes(this.getFullRoute('confirmParameters')) ||
            routeMatchesRouteTemplate(
                route,
                this.getFullRouteWithApplicationId(
                    'confirmParametersWithApplicationId',
                    this.applicationIdParameter,
                ),
            )
        );
    }

    static isPurposeOfFilingRoute(route: string): boolean {
        return route.includes(this.getFullRoute('purposeOfFiling'));
    }

    static purposeOfFiling = `${this.newApplicationBaseUrl}/purpose-of-filing`;
    static parameters = `${this.newApplicationBaseUrl}/parameters`;
    static confirmParameters = `${this.newApplicationBaseUrl}/confirm-parameters`;

    static parametersWithApplicationId = `${this.applicationIdParameter}/parameters`;
    static confirmParametersWithApplicationId = `${this.applicationIdParameter}/confirm-parameters`;

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof GoRoutes>,
        applicationId: string,
    ): string {
        return `/${this.applicationRootUrl}/${GoRoutes[route]}`.replace(
            this.applicationIdParameter,
            applicationId,
        );
    }

    static searchProcessing = `${this.applicationIdParameter}/search-processing`;
    static frequencyResults = `${this.applicationIdParameter}/frequency-results`;
    static complete = `${this.applicationIdParameter}/complete`;

    static isSearchProcessingRoute(route: string): boolean {
        const stringTemplate = this.getFullRouteWithApplicationId(
            'searchProcessing',
            this.applicationIdParameter,
        );

        return routeMatchesRouteTemplate(route, stringTemplate);
    }

    static isFrequencyResultsRoute(route: string): boolean {
        const stringTemplate = this.getFullRouteWithApplicationId(
            'frequencyResults',
            this.applicationIdParameter,
        );

        return routeMatchesRouteTemplate(route, stringTemplate);
    }

    static isCompleteRoute(route: string): boolean {
        const stringTemplate = this.getFullRouteWithApplicationId(
            'complete',
            this.applicationIdParameter,
        );

        return routeMatchesRouteTemplate(route, stringTemplate);
    }

    static overview = `overview`;
    static help = `help`;
    static about = `about`;
    static fees = `fees`;
}

export class FrnFilingRoutes {
    static readonly rootUrl = 'frn-filing';
    static readonly applicationIdParameter = ':id';
    static readonly newFiling: string = 'new';
    static readonly typeOfFiling = `${this.applicationIdParameter}/type`;
    static readonly detail = `${this.applicationIdParameter}/detail`;

    static getFullRoute(route: RouteKey<typeof FrnFilingRoutes>): string {
        return `/${this.rootUrl}/${FrnFilingRoutes[route]}`;
    }

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof FrnFilingRoutes>,
        applicationId: string,
    ): string {
        return `/${this.rootUrl}/${FrnFilingRoutes[route]}`.replace(
            this.applicationIdParameter,
            applicationId,
        );
    }

    /**
     * Checks if the URL is specifically the FRN filing detail route.
     * @param url The URL to check.
     * @returns True if the URL matches the detail route pattern.
     */
    static isFrnFilingDetailRoute(url: string): boolean {
        const detailRoute = this.detail.replace(
            this.applicationIdParameter,
            '[^/]+',
        );

        const pattern = new RegExp(`^/${this.rootUrl}/${detailRoute}$`);
        return pattern.test(url);
    }

    /**
     * Checks if the URL is an existing FRN filing route (has an application ID).
     * This includes detail, type, and any other routes with an ID, but excludes the "new" route.
     * @param url The URL to check.
     * @returns True if the URL is an FRN filing route with an application ID.
     */
    static isExistingFrnFilingRoute(url: string): boolean {
        if (
            !url.startsWith(`/${this.rootUrl}/`) ||
            url.startsWith(`/${this.rootUrl}/${this.newFiling}`)
        ) {
            return false;
        }

        // Must have something after /frn-filing/ (the application ID)
        return url.length > `/${this.rootUrl}/`.length;
    }

    /**
     * Returns true for FRN routes where the step list should NOT allow direct navigation.
     * Currently, only the "new" page (no application id yet) should block direct navigation.
     */
    static isRouteWhereStepsCannotBeDirectlyNavigatedTo(url: string): boolean {
        const newRoute = `/${this.rootUrl}/${this.newFiling}`;
        return url.startsWith(newRoute);
    }
}

export class SSORoutes {
    static readonly rootUrl = 'sso';
    static readonly goSsoRedirect = 'go';

    static getFullRoute(route: RouteKey<typeof SSORoutes>): string {
        return `/${this.rootUrl}/${SSORoutes[route]}`;
    }
}

export const RouteConstants = {
    Application: ApplicationRoutes,
    Order: OrderRoutes,
    Payment: PaymentRoutes,
    User: UserRoutes,
    Profile: ProfileRoutes,
    Dashboard: DashboardRoutes,
    Reports: ReportsRoutes,
    Template: TemplateRoutes,
    Home: HomeRoutes,
    Go: GoRoutes,
    FrnFiling: FrnFilingRoutes,
    ServiceFulfillment: ServiceFulfillmentRoutes,
    SSO: SSORoutes,
    UserRegistration: '/registration',
    Error: '/', // This is a catch-all for any unexpected errors fow now,
    // TODO: we may want to show the users an error page or message with the ID of the error
};
